/**
 * sysconfigMigration.js -- Pure migration helpers for normalizing
 * legacy sysconfig field names.
 *
 * The migration helper does not mutate the input object.
 * It returns a new migrated object when changes are needed,
 * or returns the original object unchanged when no migration is needed.
 */

/**
 * Migrate a single EV object, returning a new object if changes were
 * needed, or the original reference if it was already canonical.
 * @returns {{ ev: Object, changed: boolean, actions: string[] }}
 */
function migrateEv(ev) {
  let current = ev;
  let changed = false;
  const actions = [];

  // 1) away-discharge kWh -> fraction
  if (
    current.ave_away_hourly_discharge_kwh != null &&
    current.ave_away_hourly_discharge_fraction == null
  ) {
    changed = true;
    const cap = Number(current.maximum_capacity_kwh);
    const { ave_away_hourly_discharge_kwh, ...rest } = current;
    if (Number.isFinite(cap) && cap > 0) {
      rest.ave_away_hourly_discharge_fraction = Math.min(
        ave_away_hourly_discharge_kwh / cap,
        1.0,
      );
    }
    current = rest;
    actions.push("ev_away_discharge_kwh_to_fraction");
  }

  // 2) strip stale csv_soc_data left by a previous (reverted) approach
  if (current.csv_soc_data != null) {
    changed = true;
    const { csv_soc_data, ...rest } = current;
    current = rest;
    actions.push("ev_drop_csv_soc_data");
  }

  return { ev: current, changed, actions };
}

/**
 * Migrate legacy sysconfig fields on every meter.
 *
 * Currently handles:
 *  1. `consumption_devices` -> `uncontrollable_loads` rename.
 *  2. Per-EV: `ave_away_hourly_discharge_kwh` -> `_fraction` conversion
 *     (kWh / maximum_capacity_kwh, clamped to 1.0).
 *  3. Per-EV: strip stale `csv_soc_data`.
 *
 * @param {Object} sysconfig
 * @returns {{
 *   sysconfig: Object,
 *   migrated: boolean,
 *   details: Array<{ meterIndex: number, action: string }>
 * }}
 */
export function migrateSysconfigFields(sysconfig) {
  const meters = sysconfig?.Microgrid?.meters;
  if (!Array.isArray(meters) || meters.length === 0) {
    return { sysconfig, migrated: false, details: [] };
  }

  let migrated = false;
  const details = [];

  const newMeters = meters.map((meter, idx) => {
    let meterChanged = false;
    let currentMeter = meter;

    // --- consumption_devices rename ---
    if (Array.isArray(currentMeter.consumption_devices)) {
      meterChanged = true;
      const { consumption_devices, ...rest } = currentMeter;

      if (Array.isArray(currentMeter.uncontrollable_loads)) {
        details.push({ meterIndex: idx, action: "conflict_resolved" });
        currentMeter = rest;
      } else {
        details.push({ meterIndex: idx, action: "renamed" });
        currentMeter = { ...rest, uncontrollable_loads: consumption_devices };
      }
    }

    // --- per-EV migrations ---
    const evs = currentMeter.evs;
    if (Array.isArray(evs) && evs.length > 0) {
      let evsChanged = false;
      const newEvs = evs.map((ev) => {
        const result = migrateEv(ev);
        if (result.changed) {
          evsChanged = true;
          result.actions.forEach((action) => {
            details.push({ meterIndex: idx, action });
          });
        }
        return result.ev;
      });

      if (evsChanged) {
        meterChanged = true;
        currentMeter = { ...currentMeter, evs: newEvs };
      }
    }

    if (meterChanged) {
      migrated = true;
    }
    return meterChanged ? currentMeter : meter;
  });

  if (!migrated) return { sysconfig, migrated: false, details: [] };

  return {
    sysconfig: {
      ...sysconfig,
      Microgrid: {
        ...sysconfig.Microgrid,
        meters: newMeters,
      },
    },
    migrated: true,
    details,
  };
}
